import { useState, useEffect } from "react";
import { ImagePlus, Loader2, Trash2, Building2 } from "lucide-react";

import { getMySchool, updateSchool, deleteSchool } from "../../api/schoolApi";

import { Card, CardHeader, CardTitle, CardContent, Badge } from "../../components/ui";
import { useToast } from "../../context/ToastContext";

const fieldClasses =
  "w-full rounded-md border border-neutral-200 dark:border-neutral-700 " +
  "bg-white dark:bg-neutral-900 px-3 py-2 text-sm " +
  "text-neutral-900 dark:text-neutral-100 " +
  "placeholder:text-neutral-400 dark:placeholder:text-neutral-500 " +
  "focus:outline-none focus:ring-2 focus:ring-neutral-300 dark:focus:ring-neutral-600 " +
  "focus:border-neutral-300 dark:focus:border-neutral-600 " +
  "transition-colors";

const EditSchool = () => {
  const [initialLoading, setInitialLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [form, setForm] = useState({
    schoolName: "",
    mobile: "",
    address: "",
  });

  const [logoFile, setLogoFile] = useState(null);
  const toast = useToast();

  const [logoPreview, setLogoPreview] = useState(null); // local blob preview
  const [currentLogoUrl, setCurrentLogoUrl] = useState(null); // saved logo from server

  // Load existing school profile on mount
  useEffect(() => {
    const fetchSchool = async () => {
      try {
        setInitialLoading(true);

        const school = await getMySchool();

        setForm({
          schoolName: school?.schoolName || "",
          mobile: school?.mobile || "",
          address: school?.address || "",
        });

        setCurrentLogoUrl(school?.logo?.url || null);
      } catch (err) {
        toast.error(
          err?.response?.data?.message || "Failed to load school profile"
        );
      } finally {
        setInitialLoading(false);
      }
    };

    fetchSchool();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);

      const formData = new FormData();
      formData.append("schoolName", form.schoolName);
      formData.append("mobile", form.mobile);
      formData.append("address", form.address);

      if (logoFile) {
        formData.append("logo", logoFile);
      }

      const data = await updateSchool(formData);

      toast.success("School profile updated successfully.");
      setCurrentLogoUrl(data?.school?.logo?.url || currentLogoUrl);
      setLogoFile(null);
      setLogoPreview(null);
    } catch (err) {
      toast.error(
        err?.response?.data?.message || "Failed to update school profile"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    const confirmed = window.confirm(
      "This will permanently delete your school profile and logo. This action cannot be undone. Continue?"
    );

    if (!confirmed) return;

    try {
      setDeleting(true);

      await deleteSchool();

      toast.success("School deleted successfully.");
      setForm({ schoolName: "", mobile: "", address: "" });
      setCurrentLogoUrl(null);
    } catch (err) {
      toast.error(
        err?.response?.data?.message || "Failed to delete school profile"
      );
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="p-3 sm:p-6 lg:p-8 max-w-3xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <div className="hidden sm:flex h-10 w-10 items-center justify-center rounded-lg bg-neutral-900 dark:bg-neutral-100">
            <Building2 className="h-5 w-5 text-white dark:text-neutral-900" />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-neutral-900 dark:text-neutral-100">
              School Profile
            </h1>
            <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5">
              Update your school's details and logo.
            </p>
          </div>
        </div>

        <Badge variant="secondary" className="w-fit text-[11px] font-medium">
          Admin Access
        </Badge>
      </div>

      {/* Main Form Card */}
      <Card className="border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-950 shadow-sm overflow-hidden">
        <CardHeader className="border-b border-neutral-100 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-900/60 px-5 py-3.5">
          <CardTitle className="text-xs font-semibold text-neutral-700 dark:text-neutral-300 uppercase tracking-wider">
            School Information & Logo
          </CardTitle>
        </CardHeader>

        <CardContent className="p-5 sm:p-7">
          {initialLoading ? (
            <div className="flex items-center gap-2 text-neutral-400 dark:text-neutral-500 text-sm py-10 justify-center">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading school profile...
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Logo Upload */}
              <div className="flex items-center gap-5">
                <div className="h-20 w-20 shrink-0 rounded-xl border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-900 flex items-center justify-center overflow-hidden p-2">
                  {logoPreview || currentLogoUrl ? (
                    <img
                      src={logoPreview || currentLogoUrl}
                      alt="School logo"
                      className="h-full w-full object-contain"
                    />
                  ) : (
                    <ImagePlus className="h-6 w-6 text-neutral-300 dark:text-neutral-600" />
                  )}
                </div>

                <div className="space-y-1.5">
                  <label
                    htmlFor="logo"
                    className="inline-flex items-center gap-1.5 cursor-pointer text-xs font-medium
                               text-neutral-700 dark:text-neutral-200
                               border border-neutral-200 dark:border-neutral-700
                               bg-white dark:bg-neutral-900
                               rounded-md px-3 py-1.5
                               hover:bg-neutral-50 dark:hover:bg-neutral-800
                               transition-colors"
                  >
                    <ImagePlus className="h-3.5 w-3.5" />
                    {currentLogoUrl ? "Change Logo" : "Upload Logo"}
                  </label>
                  <input
                    id="logo"
                    name="logo"
                    type="file"
                    accept="image/*"
                    onChange={handleLogoChange}
                    className="hidden"
                  />
                  <p className="text-[11px] text-neutral-400 dark:text-neutral-500">
                    PNG, JPG, GIF, or WEBP. Max 10MB.
                  </p>
                </div>
              </div>

              {/* School Name */}
              <div className="space-y-1.5">
                <label
                  htmlFor="schoolName"
                  className="block text-xs font-medium text-neutral-700 dark:text-neutral-300"
                >
                  School Name
                </label>
                <input
                  id="schoolName"
                  name="schoolName"
                  type="text"
                  value={form.schoolName}
                  onChange={handleChange}
                  required
                  className={fieldClasses}
                />
              </div>

              {/* Mobile */}
              <div className="space-y-1.5">
                <label
                  htmlFor="mobile"
                  className="block text-xs font-medium text-neutral-700 dark:text-neutral-300"
                >
                  Mobile Number
                </label>
                <input
                  id="mobile"
                  name="mobile"
                  type="text"
                  value={form.mobile}
                  onChange={handleChange}
                  required
                  className={fieldClasses}
                />
              </div>

              {/* Address */}
              <div className="space-y-1.5">
                <label
                  htmlFor="address"
                  className="block text-xs font-medium text-neutral-700 dark:text-neutral-300"
                >
                  Address
                </label>
                <textarea
                  id="address"
                  name="address"
                  rows={3}
                  value={form.address}
                  onChange={handleChange}
                  required
                  className={`${fieldClasses} resize-none`}
                />
              </div>

              {/* Actions */}
              <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3 pt-4 border-t border-neutral-100 dark:border-neutral-800">
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={deleting || loading}
                  className="inline-flex items-center gap-1.5 text-xs font-medium
                             text-red-600 dark:text-red-400
                             hover:text-red-700 dark:hover:text-red-300
                             disabled:opacity-50 transition-colors"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  {deleting ? "Deleting..." : "Delete School"}
                </button>

                <button
                  type="submit"
                  disabled={loading || deleting}
                  className="inline-flex items-center justify-center gap-1.5 rounded-md
                             bg-neutral-900 dark:bg-neutral-100
                             text-white dark:text-neutral-900
                             text-sm font-medium px-4 py-2
                             hover:bg-neutral-800 dark:hover:bg-neutral-200
                             disabled:opacity-50 transition-colors"
                >
                  {loading && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                  {loading ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default EditSchool;